<template>
  <div class="line-chart w-100">
    <s-loader
      v-show="showLoader"
      class="line-chart__loader"
      :class="{ 'line-chart__loader--visible': showLoader }"
      size="lg"
    />
    <div class="line-chart__chart-wrapper">
      <e-chart
        class="e-chart"
        autoresize
        ref="echarts"
        outlined
        :class="{ 'border-0': !chartData.length }"
        @legendselectchanged="changeLegend"
        @restore="onRestore"
        @rendered="onRendered"
        @magictypechanged="changeChartType"
      />
      <img
        class="e-chart__image img-fluid w-100"
        alt="chart-image"
        :src="chartImg"
      >
      <s-empty-list
        class="line-chart__empty-list"
        v-show="showEmptyList"
      />
    </div>
    <div class="display-bookmarks" v-if="oscillographyChartType
    === oscillographyChartTypes.OSCILLOGRAPHY">
      <b-checkbox
        class="chart-legends__toggle-all"
        @change="checkChangeBookmarks"
      />
      <span>
          {{ $t('general.display-bookmarks') }}
      </span>
    </div>
    <div class="line-chart__legend">
      <oscillography-chart-legends
        v-if="chartData.length && !loading"
        :key="chartLegendKey"
        :start-collapsed="startCollapsed"
        :items="legend"
        :maneuver-data="maneuverData"
        :chart-id="chartId"
        :hidden-legends="hiddenLegends"
        :items-unique-property="itemsUniqueProperty"
        :colors-list="colorsList"
        :oscillography-chart-type="oscillographyChartType"
        :bookmarks="bookmarks"
        :hide-maneuvers-section="hideManeuversSection"
        :enable-maneuver-list-controls="enableManeuverListControls"
        :hide-installation-and-company-columns="hideInstallationAndCompanyColumns"
        @toggle-legend="toggleLegendProxy"
        @update-hidden-legends="handleUpdateHiddenLegends"
        @select-all-legends="selectAllLegendsProxy"
        @unselect-all-legends="unselectAllLegendsProxy"
        @toggle-maneuver="toggleManeuver"
        @toggle-all-maneuvers="toggleAllManeuvers"
      />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import events from '@/domain/events/charts';
import chartConfiguration from '@/domain/services/charts/linesBuilderOscillography';
import getDataManager from '@/domain/services/charts/dataManagers';
import { formatNumber } from '@/services/translate';
import globalEventBus from '@/services/globalEventBus';
import cloneDeep from 'lodash/cloneDeep';
import OscillographyChartLegends from '@/components/charts/oscillography/OscillographyChartLegends';
import themeService from '@/services/themeService';
import groupBy from 'lodash/groupBy';
import chartTypes from '@/domain/enums/chartTypes';
import oscillographyChartMixin from '@/components/mixins/oscillographyChartMixin';
import exponentialFilter,
{
  checkNumberIsValidForScientificNotation,
}
from '@/filters/exponentialFilter';
import oscillographyChartTypes from '@/domain/enums/oscillographyChartTypes';

const enabledColor = themeService.getFontColor();
const disabledColor = themeService.getStyleVar('font-color-disabled');

export default {
  props: {
    chartToolbox: {
      type: [Object, Boolean],
      default: true,
    },
    xDataManager: {
      type: [String, Function],
      required: false,
    },
    title: {
      type: Object,
      required: false,
    },
    firstColumnI18nTitle: {
      type: String,
      default: 'fields.general.date-time',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    chartId: {
      type: String,
      required: true,
    },
    useUnitsForAxes: {
      type: Boolean,
      default: false,
    },
    showExceededPoints: {
      type: Boolean,
      default: false,
    },
    showDiagnosticContext: {
      type: Boolean,
      default: false,
    },
    defaultShowTooltip: {
      type: Boolean,
      default: true,
    },
    defaultShowGrid: {
      type: Boolean,
      default: true,
    },
    defaultSmooth: {
      type: Boolean,
      default: true,
    },
    removeOutlier: {
      type: Boolean,
      default: false,
    },
    enableMultiAxis: {
      type: Boolean,
      default: true,
    },
    chartType: {
      type: String,
      default: chartTypes.LINE,
      validator: value => Object.values(chartTypes).includes(value),
    },
    oscillographyChartType: {
      type: String,
      default: oscillographyChartTypes.OSCILLOGRAPHY,
    },
    hideManeuversSection: {
      type: Boolean,
      default: false,
    },
    colorsList: {
      type: Array,
    },
    startCollapsed: {
      type: Boolean,
      default: false,
    },
    timeZone: {
      type: String || null,
      default: null,
    },
    startIndexFromOne: {
      type: Boolean,
      default: false,
    },
    isOscyllography: {
      type: Boolean,
      default: false,
    },
    maneuverData: {
      type: Array,
      required: false,
    },
    enableManeuverListControls: {
      type: Boolean,
      default: false,
    },
    hideInstallationAndCompanyColumns: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    OscillographyChartLegends,
  },
  mixins: [oscillographyChartMixin],
  data: vm => ({
    chartsRefs: ['echarts'],
    showTooltip: vm.defaultShowTooltip,
    showGrid: vm.defaultShowGrid,
    smoothed: vm.defaultSmooth,
    localLoading: false,
    highlightedAxis: null,
    chartImg: null,
    tooltipListener: null,
    selectedChartType: null,
    bookmarks: true,
    oscillographyChartTypes,
  }),
  beforeCreate() {
    Object.defineProperty(this, 'chartOptions', {
      configurable: true,
      writable: true,
      value: {},
    });
  },
  computed: {
    ...mapGetters([
      'isPrinting',
    ]),
    showLoader() {
      return this.loading || this.localLoading;
    },
    showEmptyList() {
      return !this.showLoader && !this.chartData.length;
    },
    chartLegendKey() {
      if (this.oscillographyChartType !== oscillographyChartTypes.DIAGNOSTICS
        || !this.enableManeuverListControls) return '';

      return JSON.stringify(this.legend
        .map(item => item[this.itemsUniqueProperty])
        .sort());
    },
    turnOffBoundaryGap() {
      return this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS;
    },
  },
  mounted() {
    this.setChartOptions();
    this.initListeners();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    initListeners() {
      globalEventBus.on(events.toggleTooltip, this.toggleTooltip, this);
      globalEventBus.on(events.toggleGrid, this.toggleGrid, this);
      globalEventBus.on(events.toggleOutlier, this.toggleOutlier, this);
      globalEventBus.on(events.toggleSmooth, this.toggleSmooth, this);
      globalEventBus.on(events.toggleMultiAxis, this.toggleMultiAxis, this);
      this.$refs.echarts
        .delegateMethod('on', 'click', 'series.line', this.handleHighlightAxis);
    },
    removeListeners() {
      globalEventBus.off(events.toggleTooltip, this.toggleTooltip, this);
      globalEventBus.off(events.toggleGrid, this.toggleGrid, this);
      globalEventBus.off(events.toggleOutlier, this.toggleOutlier, this);
      globalEventBus.off(events.toggleSmooth, this.toggleSmooth, this);
      globalEventBus.off(events.toggleMultiAxis, this.toggleMultiAxis, this);
      this.$refs.echarts
        .delegateMethod('off', 'click', 'series.line', this.handleHighlightAxis);
    },
    resetAllYAxisStyle() {
      this.chartOptions.yAxis = this.chartOptions.yAxis
        .map(() => this.getYAxisStyleConfiguration(themeService.getFontColor()));
    },
    resetHighlightedAxis() {
      this.resetAllYAxisStyle();
      this.highlightedAxis = null;
      this.$refs.echarts.delegateMethod('setOption', this.chartOptions);
      this.handleAxisColorByLegends();
    },
    handleUpdateHiddenLegends(newList) {
      this.hiddenLegends = newList;
    },
    resetDiagnosticLegendSelection() {
      this.hiddenLegends = [];
      this.$nextTick(() => {
        if (this.$refs.echarts) {
          this.$refs.echarts.dispatchAction({ type: 'legendAllSelect' });
        }
      });
    },
    getYAxisStyleConfiguration(color) {
      return {
        axisLabel: {
          color,
        },
        axisLine: {
          lineStyle: {
            color,
          },
        },
      };
    },
    handleHighlightAxis(item) {
      if (!this.enableMultiAxis || !Array.isArray(this.chartOptions.yAxis)) {
        this.highlightedAxis = null;
        return;
      }
      const { axis } = this.chartDataForAxes[item.componentIndex];
      if (axis === this.highlightedAxis) {
        this.resetHighlightedAxis();
        return;
      }
      this.highlightAxis(axis);
    },
    highlightAxis(axis) {
      this.highlightedAxis = axis;
      this.chartOptions.yAxis = this.chartOptions.yAxis.map((yAxis, index) => {
        if (index === axis) {
          return this.getYAxisStyleConfiguration(enabledColor);
        }
        return this.getYAxisStyleConfiguration(disabledColor);
      });
      this.$refs.echarts.delegateMethod('setOption', this.chartOptions);
    },
    hideLoading() {
      this.localLoading = false;
    },
    startLoading() {
      this.localLoading = true;
    },
    toggleOutlier() {
      this.$emit('update:remove-outlier', !this.removeOutlier);
      this.$emit('toggle-outlier');
    },
    toggleSmooth(id) {
      if (id !== this.chartId) return;
      this.startLoading();
      this.smoothed = !this.smoothed;
      this.setChartOptions();
    },
    toggleMultiAxis() {
      this.$emit('update:enable-multi-axis', !this.enableMultiAxis);
      this.$emit('toggle-multi-axis');
      this.$nextTick(() => this.setChartOptions());
    },
    toggleGrid(id) {
      if (id !== this.chartId) return;
      this.startLoading();
      this.showGrid = !this.showGrid;
      this.setChartOptions();
    },
    changeChartType(chartTypeConfiguration) {
      this.selectedChartType = chartTypeConfiguration.currentType;
      this.setChartOptions();
    },
    toggleTooltip(id) {
      if (id !== this.chartId) return;
      this.startLoading();
      this.showTooltip = !this.showTooltip;
      this.setChartOptions();
    },
    setChartOptions() {
      if (!this.chartData.length) {
        this.$refs.echarts.delegateMethod('clear');
        return;
      }
      const toolbox = this.isPrinting ? null : this.chartToolbox;
      const chartType = this.selectedChartType || this.chartType;
      const addBoundaryGap = this.turnOffBoundaryGap && chartType === chartTypes.BAR;
      this.chartOptions = chartConfiguration.generate(this.bookmarks, this.chartDataForAxes, {
        legendAmount: this.legend.length,
        boundaryGap: addBoundaryGap,
        chartId: this.chartId,
        title: this.title,
        firstColumnTitle: this.firstColumnI18nTitle,
        toolbox,
        tooltipFormatter: this.tooltipFormatter,
        showTooltip: this.showTooltip,
        removeOutlier: this.removeOutlier,
        smoothed: this.smoothed,
        enableMultiAxis: this.enableMultiAxis,
        showGrid: this.showGrid,
        xDataManager: this.xDataManager,
        isPrinting: this.isPrinting,
        chartType,
        ...(this.useUnitsForAxes ? { useUnitsForAxes: true } : {}),
        ...(this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS
          ? { showExceededPoints: this.showExceededPoints }
          : {}),
        colorsList: this.colorsList,
        timeZone: this.timeZone,
        startIndexFromOne: this.startIndexFromOne,
      });
      this.$refs.echarts.delegateMethod(
        'setOption',
        this.chartOptions,
        { replaceMerge: ['tooltip', 'yAxis', 'series', 'toolbox'] },
      );
      this.highlightedAxis = null;
      this.$nextTick(() => {
        this.hideLoading();
      });
    },
    getEmptyAxis() {
      const groupedLegendsByAxis = groupBy(this.legend, 'axis');
      const groupedLegendsValues = Object.values(groupedLegendsByAxis);
      const axisValuesFilteredOfHiddenLegends = groupedLegendsValues.map(
        group => group.filter(
          legend => !this.hiddenLegends.includes(legend.name),
        ),
      );
      const emptyAxis = [];
      axisValuesFilteredOfHiddenLegends.forEach((group, axis) => {
        if (!group.length) {
          emptyAxis.push(axis);
        }
      });
      return emptyAxis;
    },
    async handleAxisColorByLegends() {
      await this.$nextTick();
      const emptyAxis = this.getEmptyAxis();
      if (!Array.isArray(this.chartOptions.yAxis)) {
        this.chartOptions.yAxis = emptyAxis.includes(0)
          ? this.getYAxisStyleConfiguration(disabledColor)
          : this.getYAxisStyleConfiguration(enabledColor);
      }
      if (Array.isArray(this.chartOptions.yAxis)) {
        this.chartOptions.yAxis = this.chartOptions.yAxis.map((yAxis, index) => {
          if (emptyAxis.includes(index)) return this.getYAxisStyleConfiguration(disabledColor);
          return this.getYAxisStyleConfiguration(enabledColor);
        });
      }
      this.highlightedAxis = null;
      this.$refs.echarts.delegateMethod('setOption', { yAxis: this.chartOptions.yAxis });
    },
    toggleLegendProxy(item) {
      this.toggleLegend(item);
      this.handleAxisColorByLegends();
    },
    selectAllLegendsProxy() {
      this.selectAllLegends();
      this.handleAxisColorByLegends();
    },
    unselectAllLegendsProxy() {
      this.unselectAllLegends();
      this.handleAxisColorByLegends();
    },
    mountTooltipValue(params) {
      const itemInfo = cloneDeep(params);
      const chartData = this.chartDataForAxes[itemInfo.seriesIndex] || {};
      const initialChartValue = Array.isArray(itemInfo.value) ? itemInfo.value : [];
      const initialXValue = initialChartValue[0] ?? itemInfo.data?.coord?.[0] ?? '';
      const indexedPoint = chartData.values?.[itemInfo.dataIndex];
      const formattedPoints = chartData.values?.map((point) => {
        const formattedX = getDataManager(this.xDataManager).format(point.x, this.timeZone);
        return { point, formattedX };
      }) || [];
      const matchingPointByValue = formattedPoints.find(({ point, formattedX }) => (
        formattedX === initialXValue
        && Number(point.y) === Number(initialChartValue[1])
      ))?.point;
      const matchingPoint = matchingPointByValue
        || formattedPoints.find(({ formattedX }) => formattedX === initialXValue)?.point
        || formattedPoints.find(({ point }) => (
          Number(point.y) === Number(initialChartValue[1])
        ))?.point;
      const indexedPointHasLimits = (
        indexedPoint?.limiteInferior !== null
        && indexedPoint?.limiteInferior !== undefined
      ) || (
        indexedPoint?.limiteSuperior !== null
        && indexedPoint?.limiteSuperior !== undefined
      );
      const dataPoint = itemInfo.componentType === 'markPoint'
        ? itemInfo.data
        : {
            ...matchingPoint,
            ...indexedPoint,
            ...(!indexedPointHasLimits ? {
              limiteInferior: matchingPoint?.limiteInferior,
              limiteSuperior: matchingPoint?.limiteSuperior,
            } : {}),
          };
      const parseLimit = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const parsedValue = Number(String(value).replace(',', '.'));
        return Number.isFinite(parsedValue) ? parsedValue : null;
      };
      const lowerLimit = parseLimit(dataPoint?.limiteInferior);
      const upperLimit = parseLimit(dataPoint?.limiteSuperior);
      const exceededPoint = itemInfo.componentType === 'markPoint'
        || dataPoint?.limiteExcedido === true
        || dataPoint?.exceeded === true
        || lowerLimit !== null
        || upperLimit !== null
        ? dataPoint
        : null;
      if (exceededPoint?.coord) {
        itemInfo.value = exceededPoint.coord;
      }
      const chartValue = Array.isArray(itemInfo.value) ? itemInfo.value : [];
      const xValue = chartValue[0] ?? '';
      const rawYValue = chartValue[1];
      const unit = this.useUnitsForAxes && typeof chartData.unit === 'string'
        ? chartData.unit
        : '';
      const chartAxis = this.chartOptions.series?.[itemInfo.seriesIndex]?.yAxisIndex
        ?? chartData.axis
        ?? 0;
      const INDEX_CORRECTION_TO_NUMBER = 1;
      const axisDescription = this.useUnitsForAxes && unit
        ? `${this.$t('e-charts.y-axis-unit')}: ${unit}`
        : `${this.$t('e-charts.y-axis-index')}: ${chartAxis + INDEX_CORRECTION_TO_NUMBER}`;
      const fractionDigits = String(rawYValue ?? '').length;

      let formattedYValue = '';
      if (rawYValue !== null && rawYValue !== undefined) {
        formattedYValue = checkNumberIsValidForScientificNotation(rawYValue)
          ? exponentialFilter(rawYValue, 2)
          : formatNumber(rawYValue, {
              minimumFractionDigits: 1,
              maximumFractionDigits: fractionDigits,
            });
      }

      let maneuverName = '';
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        maneuverName = this.maneuverData.find(m => m.maneuverKey === dataPoint?.maneuver)?.name || '';
        maneuverName = maneuverName || this.maneuverData.find(m => {
          const maneuverDate = new Date(m.dateTimeOscillography);
          const [datePart, timePartRaw] = String(xValue).split(', ');
          if (!datePart || !timePartRaw) return false;

          const [p1, p2, p3] = datePart.split('/');
          const timePart = timePartRaw.trim();

          let day;
          let month;
          let year;
          // eslint-disable-next-line radix
          if (parseInt(p1) > 12) {
            day = p1;
            month = p2;
            year = p3;
          } else {
            month = p1;
            day = p2;
            year = p3;
          }

          let hours;
          let minutes;
          let seconds;
          let ampm = null;

          if (timePart.includes('AM') || timePart.includes('PM')) {
            const [timeOnly, meridian] = timePart.split(' ');
            ampm = meridian.toUpperCase();
            [hours, minutes, seconds] = timeOnly
              .split(':')
              .map(v => parseInt(v, 10));

            if (ampm === 'PM' && hours < 12) hours += 12;
            if (ampm === 'AM' && hours === 12) hours = 0;
          } else {
            [hours, minutes, seconds] = timePart
              .split(':')
              .map(v => parseInt(v, 10));
          }

          const itemDate = new Date(
            // eslint-disable-next-line radix
            parseInt(year),
            // eslint-disable-next-line radix
            parseInt(month) - 1,
            // eslint-disable-next-line radix
            parseInt(day),
            hours,
            minutes,
            seconds,
          );

          return Math.abs(maneuverDate.getTime() - itemDate.getTime()) < 1000;
        })?.name;
        const originalSeriesName = itemInfo.seriesName || chartData.name || '';
        const maneuverRegex = /(Manobra|Maniobra|Maneuver|Manöver|Manovra):?\s*\d+/i;
        const updatedSeriesName = maneuverRegex.test(originalSeriesName)
          ? originalSeriesName.replace(maneuverRegex, `${maneuverName || 'N/A'}`)
          : [originalSeriesName, maneuverName].filter(Boolean).join(' - ');

        return `
          ${itemInfo.marker}${this.showDiagnosticContext ? ` ${updatedSeriesName}` : ''}<br/>
          X: ${xValue}<br/>
          Y: ${formattedYValue} ${unit}<br/>
          ${exceededPoint ? this.getExceededPointTooltip(exceededPoint, unit) : ''}${axisDescription}
        `;
      }
      return `
        ${itemInfo.marker} ${itemInfo.seriesName}<br/>
        X: ${xValue}<br/>
        Y: ${formattedYValue} ${unit}<br/>
        ${exceededPoint ? this.getExceededPointTooltip(exceededPoint, unit) : ''}${axisDescription}
      `;
    },
    getExceededPointTooltip(point, unit) {
      if (!point) return '';

      const limits = [
        point.limiteInferior !== null && point.limiteInferior !== undefined
          ? `${this.$t('engineering.oscillography-analysis.diagnostics.lower-limit')}: ${point.limiteInferior} ${unit}`
          : '',
        point.limiteSuperior !== null && point.limiteSuperior !== undefined
          ? `${this.$t('engineering.oscillography-analysis.diagnostics.upper-limit')}: ${point.limiteSuperior} ${unit}`
          : '',
      ].filter(Boolean);

      return limits.length ? `${limits.join('<br/>')}<br/>` : '';
    },
    tooltipFormatter(params, ticket, callback) {
      if (!this.showTooltip) return null;

      if (this.tooltipListener) {
        clearTimeout(this.tooltipListener);
      }

      const tooltipValue = this.mountTooltipValue(params, ticket, callback);
      this.tooltipListener = setTimeout(() => {
        callback(ticket, tooltipValue);
      }, 500);
      return this.$t('general.loading');
    },
    restoreLegends() {
      this.hiddenLegends = [];
      this.setChartOptions();
    },
    onRestore() {
      this.selectedChartType = this.chartType;
      this.highlightedAxis = null;
      this.restoreLegends();
    },
    onRendered() {
      if (!this.isPrinting) return;

      this.$nextTick(() => {
        this.chartImg = this.$refs.echarts.getDataURL();
      });
    },
    checkChangeBookmarks(event) {
      if (event) {
        this.bookmarks = false;
        this.setChartOptions();
      } else {
        this.bookmarks = true;
        this.setChartOptions();
      }
    },
    toggleManeuver(maneuverKey) {
      this.$emit('toggle-maneuver', maneuverKey);
    },
    toggleAllManeuvers(event) {
      this.$emit('toggle-all-maneuvers', event);
    },
  },
  watch: {
    chartData() {
      this.setChartOptions();
    },
    chartLegendKey() {
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS
        && this.enableManeuverListControls) {
        this.resetDiagnosticLegendSelection();
      }
    },
    showExceededPoints() {
      if (this.oscillographyChartType === oscillographyChartTypes.DIAGNOSTICS) {
        this.setChartOptions();
      }
    },
    isPrinting(isPrinting) {
      if (!isPrinting) {
        this.chartImg = null;
      }
      const toolbox = null;
      this.setChartOptions();
      this.$refs.echarts.delegateMethod('setOption', { toolbox });
    },
  },
};
</script>
<style lang="sass" scoped>
@import "src/styles/defaults/variables"

.line-chart
  position: relative
  min-height: 100px
  outline: none
  padding-bottom: 30px
  &__loader
    position: absolute
    min-height: 100px
    height: 100%
    width: 100%
    background-color: var(--color-modal-background)
    z-index: 1

  &__empty-list
    position: absolute
    top: 50%
    left: 50%
    transform: translate(-50%, -50%)

  &__chart-wrapper
    position: relative
    width: 100%
    overflow-x: auto
    page-break-inside: avoid


.e-chart
  display: block
  margin-bottom: 15px
  min-width: 100%
  min-height: 100px
  overflow: hidden
  outline: none
  border: 1px solid $color-border-gray
  border-radius: 3px


  &__image
    display: none

  ::v-deep textarea
    white-space: pre

.display-bookmarks
  display: flex
  flex-direction: row
  padding: 10px 0 30px 0

@media print
  .e-chart
    display: none

    &__image
      display: block
</style>
